/** Month-grid helpers for Sandbox office-hours booking (same pattern as Meeteor). */

export type BookingCalendarConfig = {
    timezone?: string;
    min_notice_hours?: number;
    max_advance_days?: number;
    availability?: Array<{ day: number; enabled?: boolean }>;
    date_overrides?: Array<{ date: string; available?: boolean }>;
};

export function ymd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Month grid cells: null = padding, Date = day in month (Mon-first). */
export function getMonthGrid(year: number, monthIndex: number): (Date | null)[][] {
    const first = new Date(year, monthIndex, 1);
    const last = new Date(year, monthIndex + 1, 0);
    const startPad = (first.getDay() + 6) % 7;
    const daysInMonth = last.getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(new Date(year, monthIndex, d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
        rows.push(cells.slice(i, i + 7));
    }
    return rows;
}

function overrideForDate(config: BookingCalendarConfig, dateStr: string) {
    return config.date_overrides?.find(x => x.date === dateStr);
}

/** Whether a calendar day can be selected (before fetching slots). */
export function isDateSelectable(day: Date, config: BookingCalendarConfig, now: Date = new Date()): boolean {
    const dateStr = ymd(day);
    const minNotice = config.min_notice_hours ?? 1;
    const maxAdvance = config.max_advance_days ?? 14;

    const startOfDay = new Date(day);
    startOfDay.setHours(0, 0, 0, 0);
    const msMin = minNotice * 60 * 60 * 1000;
    if (startOfDay.getTime() + 86400000 - 1 < now.getTime() + msMin) {
        return false;
    }

    const maxEnd = new Date(now);
    maxEnd.setDate(maxEnd.getDate() + maxAdvance);
    if (startOfDay > maxEnd) return false;

    const ov = overrideForDate(config, dateStr);
    if (ov && ov.available === false) return false;
    if (ov && ov.available === true) return true;

    const avail = config.availability;
    if (avail?.length) {
        const jsDay = day.getDay();
        const rule = avail.find(a => a.day === jsDay);
        if (rule && !rule.enabled) return false;
    }

    return true;
}
